import { ChevronLeft, type LucideIcon } from 'lucide-react'
import type { ReactElement } from 'react'
import { Link } from 'react-router'

import { cn } from '@/lib/utils'

export type BottomNavBarBack = {
  to: string
  label: string
  viewTransition?: boolean
}

export type BottomNavBarLinkItem = {
  kind: 'link'
  label: string
  to: string
  icon: LucideIcon
  current?: boolean
}

export type BottomNavBarButtonItem = {
  kind: 'button'
  label: string
  icon: LucideIcon
  onClick: () => void
}

export type BottomNavBarItem = BottomNavBarLinkItem | BottomNavBarButtonItem

export type BottomNavBarProps = {
  back?: BottomNavBarBack
  items: BottomNavBarItem[]
  spacerWhenNoBack?: boolean
}

const itemClassName =
  'flex min-w-0 flex-col items-center gap-0.5 active:opacity-70 [-webkit-tap-highlight-color:transparent]'

const labelClassName = 'text-[10px] leading-none truncate'

export function BottomNavBar({ back, items, spacerWhenNoBack }: BottomNavBarProps): ReactElement {
  return (
    <nav className="bg-background/95 fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur pb-[env(safe-area-inset-bottom)] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
      <div className="mx-auto flex h-14 max-w-md items-center justify-between gap-1">
        {back !== undefined ? (
          <Link
            to={back.to}
            viewTransition={back.viewTransition}
            aria-label={`‹ ${back.label}`}
            className={cn(itemClassName, 'text-primary')}
          >
            <ChevronLeft aria-hidden="true" className="size-5" />
            <span className={cn(labelClassName, 'max-w-20 truncate')}>{back.label}</span>
          </Link>
        ) : spacerWhenNoBack === true ? (
          <span aria-hidden="true" className="w-16" />
        ) : null}
        {items.map((item) => {
          const Icon = item.icon
          if (item.kind === 'link') {
            return (
              <Link
                key={item.label}
                to={item.to}
                aria-current={item.current === true ? 'page' : undefined}
                className={cn(
                  itemClassName,
                  'hover:text-foreground',
                  item.current === true ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                <Icon aria-hidden="true" className="size-5" />
                <span className={labelClassName}>{item.label}</span>
              </Link>
            )
          }
          return (
            <button
              key={item.label}
              type="button"
              onClick={item.onClick}
              className={cn(itemClassName, 'text-muted-foreground')}
            >
              <Icon aria-hidden="true" className="size-5" />
              <span className={labelClassName}>{item.label}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
